import { Smile } from 'lucide-react';
import { type ComponentProps, lazy, Suspense, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import { Spinner } from '@apps/web/components/ui/spinner';
import { useTheme } from '@apps/web/lib/theme_store';

// The picker ships its own emoji index and rendering, so it is only fetched
// once someone actually opens it.
const EmojiPicker = lazy(async () => {
    const [{ default: Picker }, { default: data }] = await Promise.all([
        import('@emoji-mart/react'),
        import('@emoji-mart/data'),
    ]);

    return {
        default: (props: Omit<ComponentProps<typeof Picker>, 'data'>) => (
            <Picker {...props} data={data} />
        ),
    };
});

type EmojiSelection = {
    readonly native?: string;
};

export function MessageEmojiPicker({
    disabled,
    onSelect,
}: {
    readonly disabled: boolean;
    readonly onSelect: (emoji: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const theme = useTheme();

    return (
        <Popover onOpenChange={setOpen} open={open}>
            <PopoverTrigger asChild={true}>
                <Button
                    aria-label="Add emoji"
                    className="size-8"
                    disabled={disabled}
                    size="icon"
                    type="button"
                    variant="ghost"
                >
                    <Smile className="size-4" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-auto border-none p-0 shadow-none"
                side="top"
            >
                <Suspense
                    fallback={
                        <div className="bg-popover flex size-16 items-center justify-center rounded-md border">
                            <Spinner className="size-4" />
                        </div>
                    }
                >
                    <EmojiPicker
                        onEmojiSelect={(emoji: EmojiSelection) => {
                            if (emoji.native) {
                                onSelect(emoji.native);
                            }
                            setOpen(false);
                        }}
                        previewPosition="none"
                        skinTonePosition="none"
                        theme={theme === 'system' ? 'auto' : theme}
                    />
                </Suspense>
            </PopoverContent>
        </Popover>
    );
}
